import React from 'react'

/**
 * Reusable Button component
 * @param {string} variant - one of 'primary', 'secondary', 'success', 'error', 'warning', 'info', 'ghost'
 * @param {string} size - one of 'sm', 'md', 'lg'
 * @param {boolean} loading - shows inline spinner and disables the button
 * @param {string} as - HTML tag or component to render (e.g. 'a', Link)
 */
export default function Button({ variant = 'primary', size = 'md', loading = false, as: Component = 'button', children, className = '', disabled, ...props }) {
  const base = 'inline-flex items-center justify-center font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 transition'
  const variants = {
    primary: 'bg-primary hover:bg-primary-hover text-white focus:ring-primary',
    secondary: 'bg-accent hover:bg-accent-hover text-white focus:ring-accent',
    success: 'bg-success hover:bg-success-hover text-white focus:ring-success',
    error: 'bg-error hover:bg-error-hover text-white focus:ring-error',
    warning: 'bg-warning hover:bg-warning-hover text-white focus:ring-warning',
    info: 'bg-info hover:bg-info-hover text-white focus:ring-info',
    ghost: 'bg-transparent text-primary hover:bg-primary-hover hover:text-white focus:ring-primary',
  }
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  }
  const variantClasses = variants[variant] || variants.primary
  const sizeClasses = sizes[size] || sizes.md
  const isDisabled = disabled || loading

  return (
    <Component
      className={`${base} ${variantClasses} ${sizeClasses} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
      aria-disabled={isDisabled}
      disabled={Component === 'button' ? isDisabled : undefined}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </Component>
  )
}
